import { DateTime } from '@ksyos/date-time';
import { Box } from '@ksyos/design-system';
import { useState } from 'react';
import { styled } from 'styled-components';
import { AddendumButton } from '@frontend/components/elements/carePathButtons/AddendumButton';
import { ReloadEpisode } from '@frontend/components/pages/episodes/reloadEpisode';
import type { ICustomControlDiagnosticReportReadProps } from '../../interfaces/customControlDiagnosticReportReadProps';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { GradingSummary } from '../../../../../../common/enums/gradingSummary';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { translateKey } from '../../../../../translate';
import { DiagnosticReportReadButtons } from '../../../../elements/carePathButtons/DiagnosticReportReadButtons';
import { UserTextParagraph } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { TfoV1DiagnosticReportAddendumAdd } from './TfoV1DiagnosticReportAddendumAdd';
import { TfoV1DiagnosticReportAddendumRead } from './TfoV1DiagnosticReportAddendumRead';
import { TfoV1DiagnosticReportReadCustomControlFields } from './TfoV1DiagnosticReportReadCustomControlFields';

const StyledButtonContainer = styled.div`
    display: flex;
    justify-content: end;
    margin-bottom: 1rem;

    .btn-container {
        margin: auto;
    }
`;

export function TfoV1DiagnosticReportRead(props: ICustomControlDiagnosticReportReadProps) {
    const [isAddingAddendum, setIsAddingAddendum] = useState(false);

    return (
        <ProcessContainer isActiveActivity={props.itemState.initialIsOpen}>
            <ProcessItemHeader
                date={props.diagnosticReport && props.diagnosticReport.dateSent}
                isFirstItem={props.isFirstItem}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) =>
                    await props.updateItemState({
                        ...props.itemState,
                        isOpen: isOpened || props.itemState.initialIsOpen === true,
                    })
                }
                organizationName={props.diagnosticReport?.performerOrganization?.name}
                practitionerName={
                    props.diagnosticReport.performerPractitioner
                        ? getDisplayName(props.diagnosticReport.performerPractitioner)
                        : undefined
                }
                process={props.process}
                status={
                    (props.diagnosticReport.customControlData?.addendum?.gradingSummary ??
                        props.diagnosticReport.gradingSummary) === GradingSummary.deviant
                        ? translateKey('diagnosis.deviant')
                        : undefined
                }
                title={getServiceLabel(
                    props.process.code,
                    'diagnosticReport',
                    translateKey('diagnosticReport.category_examinationReport')
                )}
            />

            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                <TfoV1DiagnosticReportReadCustomControlFields
                    customControlData={props.diagnosticReport.customControlData}
                />

                {props.diagnosticReport.remark && (
                    <HorizontalFormGroupRead>
                        <FormLabel>{translateKey('diagnosticReport.remarks')}</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>{props.diagnosticReport.remark}</UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}
                {props.diagnosticReport.customControlData.addendum && (
                    <Box
                        paddingBottom={4}
                        paddingTop={4}
                    >
                        <TfoV1DiagnosticReportAddendumRead diagnosticReport={props.diagnosticReport} />
                    </Box>
                )}
                <>
                    {!isAddingAddendum && (
                        <StyledButtonContainer>
                            <>
                                {props.processButtons ? (
                                    <>{props.processButtons}</>
                                ) : (
                                    <DiagnosticReportReadButtons {...props} />
                                )}
                            </>
                            {!props.diagnosticReport.customControlData.addendum
                                ? [
                                      <AddendumButton
                                          dateSent={
                                              props.diagnosticReport.dateSent
                                                  ? DateTime.from(props.diagnosticReport.dateSent)
                                                  : undefined
                                          }
                                          key="addendumButton"
                                          onAddAddendum={() => setIsAddingAddendum(true)}
                                          performerPractitionerId={props.diagnosticReport.performerPractitionerId}
                                          sx={{ marginLeft: '1em', minWidth: 'max-content' }}
                                      />,
                                  ]
                                : undefined}
                        </StyledButtonContainer>
                    )}
                    {isAddingAddendum && (
                        <TfoV1DiagnosticReportAddendumAdd
                            diagnosticReport={{
                                ...props.diagnosticReport,
                                customControlData: props.diagnosticReport.customControlData,
                            }}
                            onCancel={() => setIsAddingAddendum(false)}
                            onSaved={async () => {
                                setIsAddingAddendum(false);
                                await props.reloadProcess?.(ReloadEpisode.reloadAll);
                            }}
                        />
                    )}
                </>
            </CustomControlCollapseBody>

            {!props.itemState.initialIsOpen && (
                <ProcessItemFooter
                    isItemOpen={props.itemState.isOpen}
                    labelLess={translateKey('diagnosticReport.hideExaminationReport')}
                    labelMore={translateKey('diagnosticReport.showExaminationReport')}
                    onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                />
            )}
        </ProcessContainer>
    );
}
